import { CommunicationPreference, States } from "../../../__generated__/types";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import {
  useUserContext,
  useUserContextDispatch,
} from "../../../hooks/userContext";
import { OverlayLoader } from "../../../components/overlay-loader";

export const UpdatingUserState = ({
  firstName,
  lastName,
  phoneNumber,
  businessAddress,
  businessName,
  businessZipCode,
  businessCity,
  businessState,
  businessWebsite,
  communicationPreference,
  workLocation,
  designerId,
}: {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  businessAddress: string;
  businessName: string;
  businessZipCode: string;
  businessCity: string;
  businessState: States;
  businessWebsite: string;
  communicationPreference: CommunicationPreference[];
  workLocation: string[];
  designerId: string;
}) => {
  
  const { user } = useUserContext();
  const { updateDesignerAccountData } = useUserContextDispatch();
  

  const handleUpdateAccount = useCallback(async () => {
    try {
      const result = await updateDesignerAccountData({
        _id: designerId,
        firstName,
        lastName,
        phoneNumber,
        businessAddress,
        businessName,
        businessZipCode,
        businessCity,
        businessState,
        businessWebsite,
        communicationPreference,
        workLocation,
      });
      return result;
    } catch (error) {
      throw error;
    }
  }, [
    designerId,
    firstName,
    lastName,
    phoneNumber,
    businessAddress,
    businessName,
    businessZipCode,
    businessCity,
    businessState,
    businessWebsite,
    communicationPreference,
    workLocation,
    updateDesignerAccountData,
  ]);

  const navigate = useNavigate();

  const hasTriggered = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessing = useRef(false);
  
  // Trigger mutation immediately on mount - ONCE ONLY
  useEffect(() => {
    // Double-check: only run if never triggered AND not currently processing
    if (!hasTriggered.current && !isProcessing.current) {
      hasTriggered.current = true;
      isProcessing.current = true;
      
      // Set a 5-second safety timeout
      timeoutRef.current = setTimeout(() => {
        isProcessing.current = false;
        navigate("/your-hub");
      }, 5000);
      
      handleUpdateAccount().then((result) => {
        isProcessing.current = false;
        
        // Clear timeout since we're navigating now
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // Navigate immediately after successful update
        // Small delay to ensure context has updated
        setTimeout(() => {
          navigate("/your-hub");
        }, 300);
      }).catch((error) => {
        isProcessing.current = false;
        // Clear timeout and navigate immediately on error
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        // Navigate to your-hub (onboarding screen) instead of dashboard
        navigate("/your-hub");
      });
    } else {
    }
    
    // DO NOT reset on unmount - we want the ref to persist across StrictMode remounts
    // return () => {
    //   hasTriggered.current = false;
    // };
  }, []); // Empty deps - only run on initial mount
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return <OverlayLoader loadingText="Creating Your Profile" />;
};
